/**
 * Date preset helpers ported from `dashboard.html:1866-1921`.
 *
 * Preset contract (all dates use the browser's local calendar):
 *   - `today`       → [today, today] — empty until sync has rows for this date
 *   - `yesterday`   → [today-1, today-1]
 *   - `last7`       → [today-7, yesterday] (last 7 complete days; excludes today)
 *   - `thisweek`    → Monday of the current week through today (ISO week start)
 *   - `last30`      → PREVIOUS calendar month (1st to last day).
 *                     Label text is "Last Month". NOT a rolling 30
 *                     days — this is a parity-critical deviation
 *                     from the button text. See parity file.
 *   - `thismonth`   → 1st of current month to last day of current month.
 *
 * Display label rules (right-side date control + mobile picker trigger):
 *   - `today` / `yesterday` / single-day ranges → `MMM D, YYYY` (local calendar)
 *   - multi-day ranges → `MMM D, YYYY - MMM D, YYYY` (or compact same-month form)
 */

export { formatCalendarDate, labelFromIsoRange } from "@/lib/dates"
import { formatCalendarDate, labelFromIsoRange } from "@/lib/dates"

export type DashboardDatePreset =
  | "today"
  | "yesterday"
  | "last7"
  | "thisweek"
  | "last30"
  | "thismonth"
  | "custom"

export interface PresetRange {
  startDate: string
  endDate: string
  displayLabel: string
}

export function computePresetRange(
  preset: DashboardDatePreset,
  now: Date = new Date(),
): PresetRange {
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  let start = new Date(today)
  let end = new Date(today)

  if (preset === "yesterday") {
    start.setDate(start.getDate() - 1)
    end = new Date(start)
  } else if (preset === "last7") {
    // Last 7 complete days ending yesterday (today excluded).
    end.setDate(end.getDate() - 1)
    start = new Date(end)
    start.setDate(start.getDate() - 6)
  } else if (preset === "thisweek") {
    // Monday of the current week through today (Sun → back 6 days).
    const day = today.getDay() // 0=Sun … 6=Sat
    const daysFromMonday = day === 0 ? 6 : day - 1
    start = new Date(today)
    start.setDate(today.getDate() - daysFromMonday)
    end = new Date(today)
  } else if (preset === "last30") {
    // Previous calendar month (1st to last day).
    const y = today.getFullYear()
    const m = today.getMonth()
    const lastMonthYear = m === 0 ? y - 1 : y
    const lastMonthNum = m === 0 ? 11 : m - 1
    start = new Date(lastMonthYear, lastMonthNum, 1)
    end = new Date(lastMonthYear, lastMonthNum + 1, 0)
  } else if (preset === "thismonth") {
    start = new Date(today.getFullYear(), today.getMonth(), 1)
    end = new Date(today.getFullYear(), today.getMonth() + 1, 0)
  }

  const displayLabel = labelFromIsoRange(
    formatCalendarDate(start),
    formatCalendarDate(end),
  )

  return {
    startDate: formatCalendarDate(start),
    endDate: formatCalendarDate(end),
    displayLabel,
  }
}

export function runPreset(
  preset: DashboardDatePreset,
  apply: (
    p: DashboardDatePreset,
    start: string,
    end: string,
    label: string,
  ) => void,
) {
  const r = computePresetRange(preset)
  apply(preset, r.startDate, r.endDate, r.displayLabel)
}
